import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import type { Account, LocalSettingsState, Modal } from '@ps/web-core-store';
import { get } from 'lodash';
import type { DeepPartial } from 'redux';

import { NotificationType } from '@swf/components/StarsMPNotification/enums';
import { getTransformedInputData as transformMPNotification } from '@swf/components/StarsMPNotification/utils';
import { NotificationServiceType, SuspendAccountCode } from '@swf/core/enums';
import type {
  BaseMPNotification,
  DisplayValuePropositionNotification,
  ForceLogout,
  GeoComplyPLC,
  InactivityMessage,
  InactivityNotification,
  LobbyNotification,
  LoginEventNotification,
  Notification,
  NotificationMessage,
  PSNPartnerNotification,
  RawNotification,
  SeUpdateNotification,
  SpendBudgetNotification,
  SuspendAccount,
  UKAccountInfoNotification
} from '@swf/core/types/Notification';
import { LoginEventType, NotificationCode, TokenInvalidReason } from '@swf/core/types/Notification';

import type { NotificationModel } from '../models/notification';
import type { Dispatch, ExtractDispatchFromModel } from '../store';

interface Props {
  accountUpdate?(props: Partial<Account>): void;
  addCleared?(props: Notification): void;
  addExpiring?(props: Notification): void;
  addIssued?(props: Notification): void;
  body: NotificationMessage['body'];
  currentModal: string;
  features: Partial<{ [key in FeatureFlag]: boolean }>;
  geoComplyPLC(props: GeoComplyPLC): void;
  getClearedById?(ticketRollId: number): Notification;
  getExpiringById?(ticketRollId: number | string): Notification;
  getIsNotificationEnabled?(notification: BaseMPNotification): Promise<boolean>;
  getIssuedById?(ticketRollId: number | string): Notification;
  getRemainingTime?(): Promise<number>;
  isEmbedded?: boolean;
  isSiteUS: boolean;
  license: LicenceId;
  locale: string;
  localSettingsSet?(props: DeepPartial<LocalSettingsState>): void;
  logout(props?: { implicit?: boolean } | object): void;
  modal: {
    close(path: string): void;
    enqueue(props: Modal): void;
    replace(props: Modal[]): void;
    unique(props: Modal): void;
    unshift(props: Modal): void;
    unshiftUnique(props: Modal): void;
  };
  notificationDispatch?: ExtractDispatchFromModel<NotificationModel, Dispatch>;
  remainingInactivitySeconds?: number;
  isSiteDE?: boolean;
  suppressMarketingNotification?(type: string): boolean;
  site: Sites;
  type: string;
  isSdk?: boolean;
}

const geoComplyVerified = 1;

const parseNotification = (rawNotification: RawNotification): Notification => {
  if (rawNotification) {
    const { clearbets, rollid, notificationid } = rawNotification;
    return {
      ...rawNotification,
      ...(clearbets ? { clearBets: clearbets } : {}),
      ...(rollid ? { rollId: rollid } : {}),
      ...(notificationid ? { notificationId: notificationid } : {})
    };
  }
  return rawNotification;
};

export const handleNotifications = async ({
  accountUpdate,
  addCleared,
  addExpiring,
  addIssued,
  body,
  currentModal,
  features,
  geoComplyPLC,
  getClearedById,
  getExpiringById,
  getIsNotificationEnabled,
  getIssuedById,
  getRemainingTime,
  isEmbedded,
  isSiteUS,
  license,
  locale,
  localSettingsSet,
  logout,
  modal,
  notificationDispatch,
  remainingInactivitySeconds,
  isSiteDE,
  site,
  suppressMarketingNotification,
  type,
  isSdk
}: Props) => {
  if (suppressMarketingNotification?.(type)) return;

  const notification = parseNotification(body.notification as RawNotification);
  const ticketRollId = `gameTicket-${get(notification, ['gameTicketId'], '')}`;
  const newGameTicketEnabled = features[FeatureFlag.WebNotificationEnabled];
  const webMPCNotificationEnabled = features[FeatureFlag.WebMPCNotificationEnabled];

  switch (type) {
    case 'gameticket.expiring':
      if (!isEmbedded && !getExpiringById?.(ticketRollId)) {
        addExpiring?.({ rollId: ticketRollId } as Notification);
        modal.enqueue({
          path: '/notifications/game-ticket/expiring',
          locationState: { ...notification, ticketRollId }
        });
      }
      break;
    case 'gameticket.issued':
      if (!isEmbedded && !getIssuedById?.(ticketRollId) && !newGameTicketEnabled) {
        addIssued?.({ rollId: ticketRollId } as Notification);
        modal.enqueue({
          path: '/notifications/game-ticket/issued',
          locationState: { ...notification, ticketRollId }
        });
      }
      break;
    case 'multipurpose.notification':
    case NotificationServiceType.MsgLobbyETCCLIUnsolicitedNotification: {
      const notificationData = notification as BaseMPNotification;
      const isNotificationEnabled = await getIsNotificationEnabled?.(notificationData);

      if (!isEmbedded && isNotificationEnabled) {
        modal.enqueue({
          path: '/mpnotifications',
          locationState: transformMPNotification(notificationData, locale)
        });
      }
      break;
    }
    case 'notification.tokenExpireWarning':
    case NotificationServiceType.IdpInactivityWarning:
    case NotificationServiceType.WTSUTokenExpireWarning: {
      if (currentModal?.includes('/eula')) {
        break;
      }
      const messageBody =
        type === NotificationServiceType.IdpInactivityWarning ? body : notification;

      const { remainingSeconds } = messageBody as InactivityNotification;

      if (remainingInactivitySeconds && remainingSeconds) {
        setTimeout(async () => {
          const remainingTime = await getRemainingTime?.();
          if (remainingTime && remainingTime <= remainingInactivitySeconds) {
            modal.unshift?.({
              path: '/notifications/inactivity-timeout-warning',
              locationState: {
                ...messageBody,
                remainingSeconds: remainingInactivitySeconds
              }
            });
          }
        }, (remainingSeconds - remainingInactivitySeconds) * 1000);
        break;
      } else {
        const path = [
          LicenceId.CAON,
          LicenceId.DE,
          LicenceId.EU,
          LicenceId.DEFAULT,
          LicenceId.GR,
          LicenceId.USMI,
          LicenceId.USPA
        ].includes(license as LicenceId)
          ? '/notifications/inactivity-timeout-warning'
          : '/notifications/inactivity-warning';

        modal.unshift?.({ path, locationState: messageBody });
        break;
      }
    }
    case 'session.logout.reply':
    case NotificationServiceType.IdpInactivityLogout: {
      const { reasonCode } = body as InactivityMessage;
      await logout?.({ implicit: true });

      if (isEmbedded) {
        window.starsweb?.auth.login();
        break;
      }

      switch (reasonCode) {
        case TokenInvalidReason.EXPLICIT_LOGOUT:
        case TokenInvalidReason.PANIC_BUTTON_FORCE_LOGOUT:
        case TokenInvalidReason.PLAYING_TIME_REACHED:
          // do nothing
          break;
        case TokenInvalidReason.NOT_ALLOWED_TO_PLAY:
          modal.enqueue({
            path: '/notifications/play-session-limit-gr',
            locationState: { msg: (body as InactivityMessage).text }
          });
          break;
        case TokenInvalidReason.GAMSTOP_FORCE_LOGOUT:
          modal.enqueue({ path: '/notifications/gamstop-exclusion' });
          break;
        case TokenInvalidReason.EXPIRE:
          modal.enqueue({
            path: '/notifications/inactivity-logout',
            locationState: notification || {
              idleSeconds: (body as InactivityMessage).idleSeconds
            }
          });
          break;
        case TokenInvalidReason.SELF_EXCLUSION_FORCE_LOGOUT:
          modal.enqueue({ path: '/notifications/account-self-excluded' });
          break;
        case TokenInvalidReason.PERMANENT_SELF_EXCLUSION_FORCE_LOGOUT:
          modal.enqueue({ path: '/notifications/permanent-self-excluded-uk' });
          break;
        case TokenInvalidReason.ADMIN_FORCE_LOGOUT:
        default:
          modal.enqueue({ path: '/notifications/force-logout' });
      }
      break;
    }
    case 'notification.account.info':
    case NotificationServiceType.MsgLobbyETCTOClientInfoNotification:
    case 'notification.user.properties':
      accountUpdate?.(body.notification as Account);
      break;
    case 'mpc.issued':
    case NotificationServiceType.MsgLobbyETCCLINotifyMPCIssued: {
      if (!webMPCNotificationEnabled) {
        modal.enqueue({ path: '/notifications/chest', locationState: notification });
      }
      break;
    }
    case 'cvl.state.notification':
    case NotificationServiceType.MsgLobbyETCCLINotifyCVLState:
      if (!isEmbedded) {
        modal.enqueue({ path: '/notifications/chest-tier', locationState: notification });
      }
      break;
    case 'freespin.expiring':
      if (!isEmbedded && !getExpiringById?.(+notification.rollId)) {
        addExpiring?.(notification);
        modal.enqueue({
          path: '/mpnotifications',
          locationState: {
            ...notification,
            type: NotificationType.FreeSpinsExpiring
          }
        });
      }
      break;
    case 'freespin.issued':
      if (!isEmbedded && !getIssuedById?.(+notification.rollId)) {
        addIssued?.(notification);
        modal.enqueue({
          path: '/mpnotifications',
          locationState: {
            ...notification,
            type: NotificationType.FreeSpinsIssued
          }
        });
      }
      break;
    case 'userroll.issued':
      if (!isEmbedded && !getIssuedById?.(+notification.rollId)) {
        addIssued?.(notification);
        modal.enqueue({
          path: '/mpnotifications',
          locationState: {
            ...notification,
            type: NotificationType.BonusIssued
          }
        });
      }
      break;
    case 'userroll.expiring':
      if (!isEmbedded && !getExpiringById?.(+notification.rollId)) {
        addExpiring?.(notification);
        modal.enqueue({
          path: '/mpnotifications',
          locationState: {
            ...notification,
            type: NotificationType.BonusExpiring
          }
        });
      }
      break;
    case 'userroll.cleared':
      if (!isEmbedded && !getClearedById?.(+notification.rollId)) {
        addCleared?.(notification);
        modal.enqueue({
          path: '/mpnotifications',
          locationState: {
            ...notification,
            type: NotificationType.BonusCleared
          }
        });
      }
      break;
    case 'notification.session.duration':
    case NotificationServiceType.MsgLobbyETCRAMROSessionDurationNotification:
      // @TODO when RO license is on the web check why WMT sends this message before set time
      if (!isEmbedded) {
        modal.enqueue({
          path: '/notifications/session-duration',
          locationState: notification
        });
      }
      break;
    case 'notification.password.change':
    case NotificationServiceType.MsgLobbyCLIPwdChangeReminder:
      modal.enqueue({
        path: '/notifications/password-change',
        locationState: notification
      });
      break;
    case 'notification.lobby.msg':
    case NotificationServiceType.MsgLobbyNotification:
      if (!isEmbedded) {
        const lobbyNotification = body.notification as LobbyNotification;

        switch (true) {
          case isSiteDE && lobbyNotification.notificationCode === NotificationCode.SessionInfo:
            modal.close?.('/notifications/hourly-notification-de');
            modal.unshift?.({
              path: '/notifications/hourly-notification-de',
              locationState: notification
            });
            break;
          case site === Sites.GR:
            localSettingsSet?.({ playSessionLimitGR: notification });
            modal.unshift?.({
              path: '/notifications/play-session-limit-gr',
              locationState: notification
            });
            break;
          case [Sites.SWE, Sites.NL].includes(site as Sites):
            modal.close?.('/notifications/logout-restrictions');
            modal.unshift?.({
              path: '/notifications/logout-restrictions',
              locationState: notification
            });
            break;
          case isSiteUS:
            modal.close('/notifications/restrict-playing-time');
            localSettingsSet?.({ restrictPlayingTime: notification });
            modal.unshift({
              path: '/notifications/restrict-playing-time',
              locationState: notification
            });
            break;
          case site === Sites.SPA &&
            [NotificationCode.SessionFinalize, NotificationCode.TimeExpireWarning].includes(
              lobbyNotification.notificationCode
            ):
            modal.close('/notifications/es-gaming-limit-messages');
            modal.unshift({
              path: '/notifications/es-gaming-limit-messages',
              locationState: notification
            });
            break;
          default:
            modal.enqueue({
              path: '/notifications/simple-notification',
              locationState: notification
            });
            break;
        }
      }
      break;
    case 'notification.session.summary':
    case NotificationServiceType.MsgLobbyETCCLISESessionNotif:
      if (!isEmbedded) {
        if (currentModal === '/notifications/session-summary-notification') {
          modal.close('/notifications/session-summary-notification');
        }

        const { notificationId } = body as RawNotification;

        switch (site) {
          case Sites.SPA:
            modal.unique({
              path: '/notifications/es-reality-check',
              locationState: notification
            });
            break;
          default:
            localSettingsSet?.({ sessionSummary: { ...notification, notificationId } });
            modal.unshift({
              path: '/notifications/session-summary-notification',
              locationState: { ...notification, notificationId }
            });
            break;
        }
      }
      break;
    case 'notification.login.event':
    case NotificationServiceType.MsgCLIFRLoginEvent:
    case NotificationServiceType.MsgCLIITLoginEvent:
    case NotificationServiceType.MsgCLILoginEvent: {
      const loginNotification = body.notification as LoginEventNotification;
      const ITLicense = license === LicenceId.ITALY;

      switch (loginNotification.event) {
        case ITLicense && LoginEventType.ValidateAccountReminder:
        case ITLicense && LoginEventType.SuspendedDocumentsSubmission:
        case ITLicense && LoginEventType.AccountVerification:
          modal.unique({
            path: '/notifications/documents-submission',
            locationState: { ...loginNotification }
          });
          break;
        case LoginEventType.ValidateAccountReminder:
          if (license === LicenceId.DENMARK) {
            modal.unique({
              path: '/notifications/validate-account-reminder',
              locationState: loginNotification
            });
          } else if ([LicenceId.ROMANIA, LicenceId.CZ].includes(license as LicenceId)) {
            localSettingsSet?.({ validateAccountDays: loginNotification.param });
            modal.unique({
              path: '/notifications/validate-account-reminder',
              locationState: loginNotification
            });
          }
          break;
        case LoginEventType.AccountVerification:
          modal.unique({ path: '/notifications/account-verification' });
          break;
      }
      break;
    }
    case 'notification.displayValueProposition':
    case NotificationServiceType.MsgLobbyETCUDisplayValueProposition:
      if (!isEmbedded) {
        const { selfExclusionExpirationDate } = notification as DisplayValuePropositionNotification;

        if (selfExclusionExpirationDate) {
          accountUpdate?.({ selfExclusionExpirationDate });
        }
        modal.unique({
          path: '/notifications/value-proposition/before-you-begin',
          locationState: { openDeposit: site === Sites.UK }
        });
      }
      break;
    case 'notification.player.blacklisted':
    case NotificationServiceType.MsgLobbyPlayerBlacklistedNotify: {
      const { code } = body.notification as SuspendAccount;
      switch (code) {
        case SuspendAccountCode.Blacklisted:
          modal.replace([
            {
              path: '/notifications/suspended-account',
              locationState: { code }
            }
          ]);
          break;
        case SuspendAccountCode.Suspended:
        case SuspendAccountCode.ExclusionList:
        case SuspendAccountCode.PlayerNotAuthorized:
          modal.enqueue({
            path: '/notifications/suspended-account',
            locationState: { code }
          });
          break;
      }
      break;
    }
    case 'notification.geocomply.locate':
    case NotificationServiceType.MsgLobbyETCGeocomplyLocate: {
      geoComplyPLC?.(body.notification as GeoComplyPLC);
      break;
    }
    case 'notification.geoip.result':
    case NotificationServiceType.MsgLobbyETCCLINotifyGeoIPResult:
      if ((body.notification as GeoComplyPLC).result !== geoComplyVerified) {
        modal.enqueue({ path: '/notifications/geo-comply', locationState: notification });
      }
      break;
    case 'notification.session.forcelogout':
    case NotificationServiceType.MsgLobbyForceClientLogout: {
      await logout?.({});
      if (site === Sites.GR) {
        localSettingsSet?.({ playSessionLimitGR: notification });
        modal.enqueue({
          path: '/notifications/play-session-limit-gr',
          locationState: notification
        });
      } else if (site === Sites.CZE) {
        const { reasonCode } = body.notification as ForceLogout;
        if (reasonCode !== TokenInvalidReason.SELF_EXCLUSION_FORCE_LOGOUT)
          modal.enqueue({
            path: '/notifications/admin-info-notification',
            locationState: notification
          });
      }
      break;
    }
    case 'notification.admin.info':
    case NotificationServiceType.MsgLobbyAdminInfo: {
      modal.enqueue({
        path: '/notifications/admin-info-notification',
        locationState: notification
      });
      break;
    }
    case 'notification.lugas.long.session':
    case NotificationServiceType.MsgLobbyETCCLINotifyLugasLongSession:
      if (!isEmbedded) {
        modal.unshiftUnique({
          path: '/notifications/playing-timeout-break',
          locationState: notification
        });
      }
      break;
    case 'notification.se.change':
    case NotificationServiceType.MsgLobbyETCSEChangeUserNotification: {
      const { selfExclusionEndDate } = notification as SeUpdateNotification;
      if (new Date(Number(selfExclusionEndDate)) < new Date()) {
        modal.replace([{ path: '/notifications/expired-self-excluded-popup' }]);
      } else {
        modal.replace([
          {
            path: '/notifications/self-excluded-popup',
            locationState: { selfExclusionEndTime: selfExclusionEndDate }
          }
        ]);
      }
      break;
    }
    case 'notification.account.reinstate':
    case NotificationServiceType.MsgLobbyETCCLINotifyAccountReinstate:
      modal.enqueue({
        path: '/notifications/reinstate-account-initial-es'
      });
      break;
    case NotificationServiceType.SpendBudget: {
      const { limitValue } = body as SpendBudgetNotification;
      if (limitValue) {
        modal.enqueue({
          path: '/notifications/spend-budget',
          locationState: { limit: limitValue }
        });
      }
      break;
    }
    case NotificationServiceType.DepositLimitsReminder: {
      modal.enqueue({
        path: '/notifications/deposit-limits-reminder'
      });
      break;
    }
    case NotificationServiceType.ParterNotification: {
      const { displayLocation } = body as PSNPartnerNotification;
      if (displayLocation == 'ACTIVE_WINDOW' || (displayLocation === 'LOBBY' && !isSdk)) {
        notificationDispatch?.addPSNPartnerNotification(body as PSNPartnerNotification);
        modal.unique({
          path: '/notifications/psn-partner',
          locationState: body
        });
      }
      break;
    }
    case NotificationServiceType.UKAccountInfoReminder: {
      const { interval } = body as UKAccountInfoNotification;
      modal.enqueue({
        path: '/notifications/account-information-reminder',
        locationState: {
          interval
        }
      });
    }
  }
};
